import { type Brand, make } from "ts-brand";
import { z } from "zod";

import { PageRequestSchema, type SortDirection } from "./pagination";

/**
 * Mandate domain model (Core 1 — Mandate Workspace). The first domain content
 * of the production rebuild; see docs/architecture.md § "Shared domain model"
 * and docs/stories/impl-mandate-list (decision D7: the Mandate DTO mirrors the
 * oracle's fields). Nominal IDs via ts-brand so the compiler can't cross
 * aggregates; Zod validates at the boundary.
 */

export type MandateId = Brand<string, "MandateId">;
export const MandateId = make<MandateId>();

export type OrganizationId = Brand<string, "OrganizationId">;
export const OrganizationId = make<OrganizationId>();

export type PersonId = Brand<string, "PersonId">;
export const PersonId = make<PersonId>();

/** Nominal ID for a per-mandate category entry. Slug-like (e.g. "lohnabrechnungen"). */
export type MandateCategoryId = Brand<string, "MandateCategoryId">;
export const MandateCategoryId = make<MandateCategoryId>();

/** A single entry in a Mandate's category catalog (per-mandate, trustee-managed). */
export const MandateCategorySchema = z.object({
    id: z.string().min(1).transform(MandateCategoryId),
    label: z.string().min(1),
});
export type MandateCategory = z.infer<typeof MandateCategorySchema>;

/** The three modes a customer may choose for outgoing e-mail notifications. */
export const NOTIFICATION_PREFERENCES = ["none", "link-only", "attachment"] as const;
export type NotificationPreference = (typeof NOTIFICATION_PREFERENCES)[number];

/**
 * The Mandate as shown in the list. `organizationId` is the customer
 * Organization that owns the Mandate (the authorization scope); `advisor` is
 * the responsible Trexpert person ("zuständige Person") — informational only,
 * never an access filter (decision D4). Code names are English ("English code,
 * German UI"); the UI labels stay German.
 */
export const MandateSchema = z.object({
    id: z.string().min(1).transform(MandateId),
    organizationId: z.string().min(1).transform(OrganizationId),
    companyName: z.string().min(1),
    industry: z.string(),
    city: z.string(),
    employeeCount: z.number().int().nonnegative(),
    advisor: z.string().min(1).transform(PersonId),
    notificationPreference: z.enum(NOTIFICATION_PREFERENCES).default("attachment"),
});

export type Mandate = z.infer<typeof MandateSchema>;

/**
 * The Mandate as shown in the Trexpert list view: the list fields plus the
 * derived «Letzte Aktivität» — the timestamp of the most recent document a
 * *customer* uploaded for this Mandate (MAX(`providedAt`) over Documents where
 * `sender === "CUSTOMER"`). Derived on read in the `listMandates` service path
 * (no mutable stored column to keep in sync; denormalising into a materialised
 * view is a future option if per-Mandate aggregation ever becomes a bottleneck).
 * Absent when the customer has never uploaded a file for this Mandate.
 */
export const MandateListItemSchema = MandateSchema.extend({
    /** ISO-8601 timestamp; absent = no customer upload ever. */
    lastCustomerActivityAt: z.string().datetime().optional(),
});

export type MandateListItem = z.infer<typeof MandateListItemSchema>;

/**
 * The mandate-list row the trustee sees: the repository projection
 * {@link MandateListItem} enriched with `openTaskListCount` — the number of
 * non-closed task lists in the Mandate («Offene Aufgaben» column). The
 * count is a cross-aggregate read the service composes *after* pagination (over
 * just the page's Mandates, via one aggregated repo call), so the Mandate
 * repository stays free of any TaskList coupling and never falls into an N+1.
 */
export const MandateListEntrySchema = MandateListItemSchema.extend({
    openTaskListCount: z.number().int().nonnegative(),
});

export type MandateListEntry = z.infer<typeof MandateListEntrySchema>;

/**
 * Default ordering of the Trexpert mandate list: descending by last customer
 * activity, with Mandates that have no customer upload sorting *last* (decision
 * D-MT-1). A stable, total comparator — ties (incl. two activity-less Mandates)
 * fall back to a case-insensitive company-name order so the list never reshuffles
 * between renders. Pure so it is unit-testable in isolation.
 */
type MandateOrderRow = Pick<MandateListItem, "companyName" | "lastCustomerActivityAt">;

export const byLastCustomerActivityDesc = (a: MandateOrderRow, b: MandateOrderRow): number => {
    const left = a.lastCustomerActivityAt;
    const right = b.lastCustomerActivityAt;
    if (left && right) {
        if (left !== right) {
            return right.localeCompare(left);
        }
    } else if (left) {
        return -1;
    } else if (right) {
        return 1;
    }
    return a.companyName.localeCompare(b.companyName, "de-CH");
};

/**
 * Columns the mandate list can be sorted by server-side. `openTaskListCount` is
 * deliberately absent: the service composes it *after* pagination (a cross-aggregate
 * read), so the page cannot be ordered by it without abandoning pagination.
 */
export const MANDATE_LIST_SORT_FIELDS = ["companyName", "lastCustomerActivityAt"] as const;
export type MandateListSortField = (typeof MANDATE_LIST_SORT_FIELDS)[number];

/**
 * Order one page's rows by the requested column, falling back to the default
 * «Letzte Aktivität» order (D-MT-1) when no sort is set. Pure, so the in-memory
 * adapter and tests share it; the PostgreSQL adapter mirrors it in SQL.
 */
export const orderMandateList = <T extends MandateOrderRow>(
    items: readonly T[],
    sortBy?: string,
    sortDir?: SortDirection
): T[] => {
    if (sortBy === "companyName") {
        const dir = sortDir === "asc" ? 1 : -1;
        return [...items].sort((a, b) => dir * a.companyName.localeCompare(b.companyName, "de-CH"));
    }
    if (sortBy === "lastCustomerActivityAt") {
        // Default sense is newest-first / activity-less last; `asc` flips it.
        const desc = [...items].sort(byLastCustomerActivityDesc);
        return sortDir === "asc" ? desc.reverse() : desc;
    }
    return [...items].sort(byLastCustomerActivityDesc);
};

/**
 * Boundary query for the paginated Trexpert mandate list (replaces the old
 * `listMandates` fetch-everything). `search` matches company name, industry or
 * city, server-side. Pages are ordered by «Letzte Aktivität» descending (D-MT-1),
 * with activity-less Mandates last and a company-name tie-break (de-CH). The
 * activity timestamp is resolved from the document store before pagination; see
 * {@link byLastCustomerActivityDesc}.
 */
export const MandateListQuerySchema = PageRequestSchema.extend({
    /**
     * «Meine»-Pill (FB-MT): when `true`, the server scopes the list to Mandates
     * the signed-in person advises (`advisor = current.userId`, resolved
     * server-side — the client never sends an identity). Absent/`false` = «Alle».
     */
    mine: z.boolean().optional(),
});
export type MandateListQuery = z.infer<typeof MandateListQuerySchema>;

/**
 * A lightweight Mandate option for filter dropdowns (e.g. the Posteingang mandate
 * filter): just the id and company name, never the full row. A bounded options
 * facet — the counterpart to fetching a paginated page — so a dropdown can list
 * every reachable Mandate without the list view's pagination.
 */
export interface MandateOption {
    id: MandateId;
    companyName: string;
}

/**
 * The Mandate as shown in its workspace header — the list fields plus the
 * agreed services ("Leistungen") and the trustee-managed category catalog.
 */
export const MandateDetailsSchema = MandateSchema.extend({
    services: z.array(z.string()),
    categories: z.array(MandateCategorySchema).default([]),
});

export type MandateDetails = z.infer<typeof MandateDetailsSchema>;

/**
 * Input for creating a new Mandate (S4 — TrexpertAdmin onboards a new
 * customer). `advisor` and `id`/`organizationId` are derived server-side: the
 * advisor from the caller's identity, the id as a UUID generated at creation
 * time. The employee count is not captured here — it is sourced from the IAM
 * module — so a new Mandate starts at {@link DEFAULT_EMPLOYEE_COUNT} until IAM
 * reports the real figure.
 */
export const CreateMandateInputSchema = z.object({
    companyName: z.string().min(1),
    industry: z.string().default(""),
    city: z.string().default(""),
});

export type CreateMandateInput = z.infer<typeof CreateMandateInputSchema>;

/** Employee count a freshly created Mandate starts with, before IAM reports the real figure. */
export const DEFAULT_EMPLOYEE_COUNT = 0;

/** The NotificationPreference a freshly created Mandate starts with. */
export const DEFAULT_NOTIFICATION_PREFERENCE: NotificationPreference = "attachment";

/**
 * Lower-case, dash-separated slug of a free-text value — the building block for
 * the derived `MandateId`/`OrganizationId`. ASCII-only by design: these ids are
 * URL- and key-safe identifiers, not display text.
 */
const slugify = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

/**
 * Transliterate Swiss Umlauts before slugging so a category like «Quellensteuer
 * Zürich» yields `quellensteuer-zuerich`, not a lossy `quellensteuer-z-rich`.
 * Category labels are German UI text, so this rule is part of the catalog's
 * domain — not a persistence concern.
 */
const slugifyLabel = (value: string): string =>
    slugify(
        value.replace(/[äöüÄÖÜ]/g, c => ({ ä: "ae", ö: "oe", ü: "ue", Ä: "Ae", Ö: "Oe", Ü: "Ue" })[c] ?? c)
    );

/**
 * Derive a unique {@link MandateCategoryId} for a new category from its label,
 * disambiguating against the existing catalog with a numeric suffix. Pure and
 * total: an all-punctuation label falls back to `kategorie`. The uniqueness
 * rule is a catalog invariant, so it lives on the aggregate — the repository
 * only stores the formed {@link MandateCategory}.
 */
export const deriveMandateCategoryId = (
    label: string,
    existing: ReadonlyArray<MandateCategory>
): MandateCategoryId => {
    const base = slugifyLabel(label) || "kategorie";
    let candidate = base;
    let counter = 1;
    while (existing.some(category => category.id === MandateCategoryId(candidate))) {
        candidate = `${base}-${counter++}`;
    }
    return MandateCategoryId(candidate);
};

/** Build a new {@link MandateCategory} with a unique id derived from its label. */
export const createMandateCategory = (
    label: string,
    existing: ReadonlyArray<MandateCategory>
): MandateCategory => ({ id: deriveMandateCategoryId(label, existing), label });

/**
 * Assemble a valid {@link MandateDetails} for a new Mandate (S4 onboarding).
 * The `MandateId` is a UUID generated server-side (never client-supplied);
 * the same UUID is used as both `MandateId` and `OrganizationId` (1:1 model).
 * The service function passes the generated ID to IAM-CDS to provision the org.
 * NotificationPreference starts at {@link DEFAULT_NOTIFICATION_PREFERENCE}, and
 * the result is validated through {@link MandateDetailsSchema}. The seed
 * `categories`/`services` are passed in (their *defaults* are catalog/config
 * concerns); the *rules* live here. The repository receives a formed aggregate.
 */
export const applyCreateMandate = (
    input: CreateMandateInput,
    advisor: PersonId,
    seed: {
        categories: ReadonlyArray<MandateCategory>;
        services: ReadonlyArray<string>;
    }
): MandateDetails => {
    const id = crypto.randomUUID();
    return MandateDetailsSchema.parse({
        id: MandateId(id),
        organizationId: OrganizationId(id),
        companyName: input.companyName,
        industry: input.industry,
        city: input.city,
        employeeCount: DEFAULT_EMPLOYEE_COUNT,
        advisor,
        services: [...seed.services],
        notificationPreference: DEFAULT_NOTIFICATION_PREFERENCE,
        categories: seed.categories.map(category => ({ ...category })),
    });
};

export const UpdateNotificationPreferenceInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    preference: z.enum(NOTIFICATION_PREFERENCES),
});

export type UpdateNotificationPreferenceInput = z.infer<typeof UpdateNotificationPreferenceInputSchema>;

/**
 * Input for a trustee setting their *own* e-mail preference for the
 * «Belege eingereicht» mail (the mail the responsible advisor receives when a
 * customer submits belege). Person-scoped, not Mandate-scoped: the target
 * {@link PersonId} is the signed-in trustee, derived server-side from the
 * session — never client-supplied — so a trustee can only change their own
 * preference. Reuses the same three {@link NOTIFICATION_PREFERENCES} modes as
 * the customer's per-Mandate preference.
 */
export const UpdateOwnNotificationPreferenceInputSchema = z.object({
    preference: z.enum(NOTIFICATION_PREFERENCES),
});

export type UpdateOwnNotificationPreferenceInput = z.infer<typeof UpdateOwnNotificationPreferenceInputSchema>;

/**
 * The editable Stammdaten of a Mandate: the company name («Firmenname»), the
 * plaintext industry/category («Branche»), the location («Ort») and the
 * responsible Trexpert person («Zuständige Person», {@link Mandate.advisor}) —
 * edited together in the «Mandat bearbeiten»-Dialog. The `id`/`organizationId`
 * are stable UUID keys assigned once at creation and never change on a rename;
 * the employee count is owned by IAM, not edited here. The `advisor` must be a
 * trustee — the service validates it against the trustee pool before persisting.
 */
export interface MandateProfileEdits {
    companyName: string;
    industry: string;
    city: string;
    advisor: PersonId;
}

/**
 * Input for editing a Mandate's Stammdaten (a Trexpert capability, gated on
 * `administer-mandate-settings`). Mirrors {@link MandateProfileEdits} plus the
 * target Mandate; `companyName` stays required (a Mandate is never nameless),
 * while `industry`/`city` may be cleared. `advisor` is the {@link PersonId} of
 * the chosen «Zuständige Person»; it must reference a trustee (enforced
 * server-side, the client only ever offers the trustee pool).
 */
export const UpdateMandateInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    companyName: z.string().min(1),
    industry: z.string().default(""),
    city: z.string().default(""),
    advisor: z.string().min(1).transform(PersonId),
});

export type UpdateMandateInput = z.infer<typeof UpdateMandateInputSchema>;

/** Input for adding a new category to a Mandate's catalog. */
export const AddMandateCategoryInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    label: z.string().min(1),
});
export type AddMandateCategoryInput = z.infer<typeof AddMandateCategoryInputSchema>;

/** Input for renaming an existing category in a Mandate's catalog. */
export const UpdateMandateCategoryInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    categoryId: z.string().min(1).transform(MandateCategoryId),
    label: z.string().min(1),
});
export type UpdateMandateCategoryInput = z.infer<typeof UpdateMandateCategoryInputSchema>;

/** Input for removing a category from a Mandate's catalog. */
export const RemoveMandateCategoryInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    categoryId: z.string().min(1).transform(MandateCategoryId),
});
export type RemoveMandateCategoryInput = z.infer<typeof RemoveMandateCategoryInputSchema>;
